import React, { useState } from 'react';
import { 
  FaFolder, 
  FaFile, 
  FaChevronRight, 
  FaChevronDown,
  FaCode,
  FaCog,
  FaDatabase,
  FaFileAlt,
  FaFileCode,
  FaFileImage,
  FaFileArchive,
  FaLayerGroup,
  FaCubes,
  FaProjectDiagram,
  FaKey
} from 'react-icons/fa';

const TreeStructure = ({ data, SendEvent }) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const toggleNode = (nodePath) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodePath)) {
      newExpanded.delete(nodePath);
    } else {
      newExpanded.add(nodePath);
    }
    setExpandedNodes(newExpanded);
  };

  const handleFileClick = (nodeData, e) => {
    e.stopPropagation();
    
    // PHP dosyası kontrolü ve action varsa SendEvent çağır
    if (nodeData.path && nodeData.name && nodeData.action) {
      const fileName = nodeData.name;
      const extension = fileName.split('.').pop()?.toLowerCase();
      
      if (extension === 'php') {
        
        // SendEvent çağır
        SendEvent({
          inputName: 'diagrun',
          message: nodeData.action
        }, []);
      }
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'php':
        return <FaFileCode className="w-3 h-3 text-blue-500" />;
      case 'js':
      case 'ts':
      case 'jsx':
      case 'tsx':
        return <FaCode className="w-3 h-3 text-yellow-500" />;
      case 'css':
      case 'scss':
        return <FaFileAlt className="w-3 h-3 text-purple-500" />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'svg':
        return <FaFileImage className="w-3 h-3 text-green-500" />;
      case 'zip':
      case 'rar':
      case 'tar':
      case 'gz':
        return <FaFileArchive className="w-3 h-3 text-orange-500" />;
      default:
        return <FaFile className="w-3 h-3 text-gray-400" />;
    }
  };

  const getFlowIcon = (flowType) => {
    switch (flowType) {
      case 'mainflow':
        return <FaProjectDiagram className="w-3 h-3 text-blue-600" />;
      case 'flows':
        return <FaLayerGroup className="w-3 h-3 text-emerald-600" />;
      case 'details':
        return <FaCog className="w-3 h-3 text-purple-600" />;
      case 'toolbar':
        return <FaCode className="w-3 h-3 text-orange-600" />;
      case 'fields':
        return <FaCubes className="w-3 h-3 text-indigo-600" />;
      default:
        return <FaFolder className="w-3 h-3 text-gray-500" />;
    }
  };

  const renderNode = (node, path = '', level = 0) => {
    const nodePath = path ? `${path}.${node}` : node;
    const isExpanded = expandedNodes.has(nodePath);
    const nodeData = path ? getNestedValue(data, nodePath) : data[node];
    
    if (!nodeData) return null;

    const isFolder = typeof nodeData === 'object' && nodeData !== null;
    const hasChildren = isFolder && Object.keys(nodeData).length > 0;
    
    const renderNodeContent = () => {
      if (nodeData.path && nodeData.name) {
        // File/Path node
        const fileName = nodeData.name;
        const extension = fileName.split('.').pop()?.toLowerCase();
        const isClickable = extension === 'php' && nodeData.action;
        
        return (
          <div 
            className={`flex items-center space-x-2 py-0.5 px-1 rounded hover:bg-gray-50 transition-colors duration-100 ${
              isClickable ? 'cursor-pointer' : 'cursor-default'
            }`}
            onClick={isClickable ? (e) => handleFileClick(nodeData, e) : undefined}
          >
            <div className="flex-shrink-0">
              {getFileIcon(nodeData.name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-gray-900 truncate">
                {nodeData.name}
              </div>
              <div className="text-xs text-gray-500 truncate font-mono">
                {nodeData.path}
              </div>
            </div>
          </div>
        );
      } else if (nodeData.PROCESS || nodeData.SCRIPT_NAME) {
        // Flow details node
        return (
          <div className="flex items-center space-x-2 py-0.5 px-1 rounded hover:bg-gray-50 transition-colors duration-100">
            <div className="flex-shrink-0">
              <FaCog className="w-3 h-3 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-gray-900 truncate">
                {nodeData.SCRIPT_NAME || nodeData.PROCESS}
              </div>
              {nodeData.SRDESC && (
                <div className="text-xs text-gray-500 truncate">
                  {nodeData.SRDESC}
                </div>
              )}
            </div>
          </div>
        );
      } else if (nodeData.ELMDC || nodeData.FNAME) {
        // Field node
        return (
          <div className="flex items-center space-x-2 py-0.5 px-1 rounded hover:bg-gray-50 transition-colors duration-100">
            <div className="flex-shrink-0">
              <FaCubes className="w-3 h-3 text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-gray-900 truncate">
                {nodeData.ELMDC || nodeData.FNAME}
              </div>
              {nodeData.DTYPE && (
                <div className="text-xs text-gray-500 truncate">
                  <span className="font-mono">{nodeData.DTYPE}</span>
                  {nodeData.REQUIRED === 'REQUIRED' && (
                    <span className="ml-1 text-red-500 font-medium">Required</span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      } else if (typeof nodeData !== 'object' || nodeData === null) {
        // Primitive value (string, number, boolean, null)
        return (
          <div className="flex items-center space-x-2 py-0.5 px-1 rounded hover:bg-gray-50 transition-colors duration-100">
            <div className="flex-shrink-0">
              <FaKey className="w-3 h-3 text-gray-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-gray-900 truncate">
                {node}: <span className="text-blue-600">{String(nodeData)}</span>
              </div>
            </div>
          </div>
        );
      } else {
        // Generic object node
        return (
          <div className="flex items-center space-x-2 py-0.5 px-1 rounded hover:bg-gray-50 transition-colors duration-100">
            <div className="flex-shrink-0">
              {getFlowIcon(node)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-gray-900 capitalize">
                {node}
              </div>
            </div>
          </div>
        );
      }
    };

    return (
      <div key={nodePath} className="select-none">
        <div 
          className={`flex items-center ${hasChildren ? 'cursor-pointer' : 'cursor-default'}`}
          onClick={() => hasChildren && toggleNode(nodePath)}
        >
          {/* Indentation */}
          <div style={{ width: `${level * 10}px` }} className="flex-shrink-0" />
          
          {/* Chevron */}
          {hasChildren && (
            <div className="flex-shrink-0 w-3 h-3 flex items-center justify-center mr-0.5">
              {isExpanded ? (
                <FaChevronDown className="w-2 h-2 text-gray-400" />
              ) : (
                <FaChevronRight className="w-2 h-2 text-gray-400" />
              )}
            </div>
          )}
          
          {/* Node Content */}
          <div className="flex-1">
            {renderNodeContent()}
          </div>
        </div>
        
        {/* Children */}
        {isExpanded && hasChildren && (
          <div className="ml-2 border-l border-gray-200">
            {Object.keys(nodeData).map((childNode) => 
              renderNode(childNode, nodePath, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  return (
    <div className="bg-white rounded border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <div className="p-0.5 bg-blue-100 rounded">
            <FaProjectDiagram className="w-3 h-3 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xs font-semibold text-gray-900">Flow Structure</h3>
            <p className="text-xs text-gray-600">
              {Object.keys(data).length} processes
            </p>
          </div>
        </div>
      </div>
      
      {/* Tree Content */}
      <div className="p-2 max-h-72 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <div className="space-y-0">
          {Object.keys(data).map((rootNode) => renderNode(rootNode))}
        </div>
      </div>
    </div>
  );
};

export default TreeStructure; 